import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { API, getToken } from "../lib/api";
import { LOGO_WIDE } from "../components/Logo";
import BrandFooter from "../components/BrandFooter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SignaturePad from "../components/SignaturePad";
import WelcomeBackBanner from "../components/WelcomeBackBanner";
import HelpRequestButton from "../components/HelpRequestButton";
import SectionTipsCard from "../components/SectionTipsCard";
import { Loader2, ChevronLeft, ChevronRight, Save, CheckCircle2, Upload, Trash2, FileText, Sparkles, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";

const isFilled = (v) => !(v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0));

function Field({ field, value, onChange }) {
  const id = `f-${field.key}`;
  if (field.key === "auth_signature") {
    return (
      <div className="space-y-2">
        <Label className="text-slate-800 font-medium">{field.label}</Label>
        {field.help && <p className="text-xs text-slate-500 leading-relaxed">{field.help}</p>}
        <SignaturePad value={value || ""} onChange={(v) => onChange(field.key, v)}/>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-slate-800 font-medium">{field.label}</Label>
      {field.help && <p className="text-xs text-slate-500 leading-relaxed">{field.help}</p>}
      {field.type === "text" || field.type === "date" || field.type === "number" ? (
        <Input id={id} type={field.type === "number" ? "number" : field.type} value={value ?? ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          className="bg-white border-slate-300 text-slate-900 focus-visible:ring-[#00C7FF]"
          data-testid={`field-${field.key}`}/>
      ) : field.type === "textarea" ? (
        <Textarea id={id} value={value ?? ""} onChange={(e) => onChange(field.key, e.target.value)} rows={3}
          className="bg-white border-slate-300 text-slate-900 focus-visible:ring-[#00C7FF]"
          data-testid={`field-${field.key}`}/>
      ) : field.type === "select" ? (
        <Select value={value || ""} onValueChange={(v) => onChange(field.key, v)}>
          <SelectTrigger data-testid={`field-${field.key}`} className="bg-white border-slate-300 text-slate-900"><SelectValue placeholder="Select…"/></SelectTrigger>
          <SelectContent className="bg-white border-slate-200">
            {field.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : field.type === "radio" ? (
        <RadioGroup value={value || ""} onValueChange={(v) => onChange(field.key, v)} className="flex flex-wrap gap-3">
          {field.options.map(o => (
            <label key={o} className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 hover:border-[#00C7FF] cursor-pointer bg-white transition">
              <RadioGroupItem value={o} id={`${id}-${o}`} data-testid={`field-${field.key}-${o}`}/>
              <span className="text-sm text-slate-800">{o}</span>
            </label>
          ))}
        </RadioGroup>
      ) : field.type === "checkbox" ? (
        <div className="flex flex-wrap gap-3">
          {field.options.map(o => {
            const arr = value || [];
            const checked = arr.includes(o);
            return (
              <label key={o} className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-300 hover:border-[#00C7FF] cursor-pointer bg-white transition">
                <Checkbox checked={checked} onCheckedChange={(c) => {
                  const next = c ? [...arr, o] : arr.filter(x => x !== o);
                  onChange(field.key, next);
                }} data-testid={`field-${field.key}-${o}`}/>
                <span className="text-sm text-slate-800">{o}</span>
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function RepeatableRows({ section, rows, onChange }) {
  // Stable per-row UUID — preserves React state when rows are added/removed/reordered.
  // Backwards-compat: legacy rows saved before this change won't have __rowKey,
  // so we lazily assign one (memoised on render via a WeakMap-ish in-place set).
  const ensureKey = (row) => {
    if (row && !row.__rowKey) row.__rowKey = `r_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
    return row.__rowKey;
  };
  rows.forEach(ensureKey);
  const addRow = () => onChange([...rows, { __rowKey: `r_${Math.random().toString(36).slice(2, 10)}_${Date.now()}` }]);
  const updateRow = (i, k, v) => { const next = [...rows]; next[i] = { ...next[i], [k]: v }; onChange(next); };
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const repFields = section.repeatable_fields || section.fields;
  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <Card key={row.__rowKey} className="bg-slate-50 border-slate-200 p-4" data-testid={`${section.repeatable_key}-row-${i}`}>
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm font-medium text-brand-primary">{section.repeatable_label} #{i + 1}</div>
            <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)} className="text-rose-600 hover:bg-rose-50" data-testid={`remove-${section.repeatable_key}-${i}`}><Trash2 size={14}/></Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {repFields.map(f => <Field key={f.key} field={f} value={row[f.key]} onChange={(k, v) => updateRow(i, k, v)}/>)}
          </div>
        </Card>
      ))}
      <Button type="button" variant="outline" onClick={addRow} className="border-dashed border-slate-300 text-slate-700 hover:bg-slate-50 w-full" data-testid={`add-${section.repeatable_key}-btn`}>
        + Add {section.repeatable_label}
      </Button>
    </div>
  );
}

export default function ClientWizard() {
  const params = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const adminOnbId = params.id;
  const jwtOnbId = params.jwtOnbId;
  const token = params.token;
  const isAdminMode = loc.pathname.startsWith("/admin/") && !!adminOnbId;
  // Preview mode is /admin/onboarding/:id/preview — same data fetch as admin
  // mode but read-only. Disables every field via a wrapping <fieldset disabled>,
  // hides save/submit buttons, and shows a violet banner explaining the mode.
  const isPreviewMode = isAdminMode && loc.pathname.endsWith("/preview");
  const isJwtClientMode = loc.pathname.startsWith("/onboarding/jwt/") && !!jwtOnbId;
  const [sections, setSections] = useState([]);
  const [onb, setOnb] = useState(null);
  const [data, setData] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tipsBySection, setTipsBySection] = useState({});
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    (async () => {
      try {
        const sectRes = await api.get(`/sections`);
        setSections(sectRes.data.sections);
        // Tips fetch in parallel — non-blocking, ok if it fails (no auth)
        api.get(`/sections/tips`).then(r => setTipsBySection(r.data.tips || {})).catch(() => {});
        let onbData;
        if (isAdminMode) {
          const r = await api.get(`/onboardings/${adminOnbId}`); onbData = r.data.onboarding;
        } else if (isJwtClientMode) {
          const r = await api.get(`/client/onboardings/${jwtOnbId}`); onbData = r.data.onboarding;
        } else {
          const r = await api.get(`/client/onboarding`, { params: { token } }); onbData = r.data.onboarding;
        }
        setOnb(onbData);
        setData(onbData.data || {});
        if (onbData.status === "submitted" && !isAdminMode) setSubmitted(true);
        const cur = onbData.current_section;
        if (cur) {
          const i = sectRes.data.sections.findIndex(s => s.id === cur);
          if (i >= 0) setIdx(i);
        }
        // After render, scroll the first empty visible field into view (resume where you left off)
        setTimeout(() => {
          const wrapper = document.querySelector("[data-resume-anchor]");
          const empty = wrapper?.querySelector('input[value=""], textarea:empty');
          (empty || wrapper)?.scrollIntoView({ behavior: "smooth", block: "center" });
          empty?.focus?.();
        }, 700);
      } catch (e) {
        toast.error(isAdminMode ? "Failed to load onboarding" : "Invalid or expired link");
      } finally { setLoading(false); }
    })();
    // Intentionally only re-runs when the route identity changes. `api`, `toast`
    // are stable module-level singletons; the inner setters are referentially
    // stable React state setters. Adding them would cause spurious refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, adminOnbId, jwtOnbId, isAdminMode, isJwtClientMode]);

  const section = sections[idx];

  const updateField = (sectionId, key, value) => {
    setData(prev => ({ ...prev, [sectionId]: { ...(prev[sectionId] || {}), [key]: value } }));
  };

  const save = async (showToast = true) => {
    if (!section) return;
    setSaving(true);
    try {
      if (isAdminMode) await api.post(`/onboardings/${adminOnbId}/admin-save`, { data: dataRef.current, current_section: section.id });
      else if (isJwtClientMode) await api.post(`/client/onboardings/${jwtOnbId}/save`, { data: dataRef.current, current_section: section.id });
      else await api.post(`/client/onboarding/save`, { token, data: dataRef.current, current_section: section.id });
      if (showToast) toast.success("Progress saved");
    } catch { if (showToast) toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!section || submitted) return;
    const i = setInterval(() => save(false), 20000);
    return () => clearInterval(i);
  }, [section, submitted]);

  const progress = useMemo(() => {
    if (!sections.length) return 0;
    let total = 0, filled = 0;
    sections.forEach(s => {
      const fs = [...(s.fields || []), ...(s.extra_fields || [])];
      fs.forEach(f => { total++; if (isFilled(data?.[s.id]?.[f.key])) filled++; });
    });
    return total ? Math.round((filled / total) * 100) : 0;
  }, [data, sections]);

  const next = async () => { await save(false); setIdx(i => Math.min(i + 1, sections.length - 1)); window.scrollTo({top:0,behavior:"smooth"}); };
  const prev = () => { setIdx(i => Math.max(i - 1, 0)); window.scrollTo({top:0,behavior:"smooth"}); };

  const submit = async () => {
    setSaving(true);
    try {
      if (isAdminMode) { await api.post(`/onboardings/${adminOnbId}/admin-submit`, { data }); toast.success("Submitted on behalf of client"); nav(`/admin/onboarding/${adminOnbId}`); }
      else if (isJwtClientMode) { await api.post(`/client/onboardings/${jwtOnbId}/submit`, { data }); setSubmitted(true); toast.success("Submitted to Sentry Cloud IT"); }
      else { await api.post(`/client/onboarding/submit`, { token, data }); setSubmitted(true); toast.success("Submitted to Sentry Cloud IT"); }
    } catch { toast.error("Submit failed"); }
    finally { setSaving(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("section", section.id); fd.append("file", file);
    try {
      let res;
      if (isAdminMode) res = await fetch(`${API}/onboardings/${adminOnbId}/admin-upload`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${getToken()}` } });
      else if (isJwtClientMode) res = await fetch(`${API}/client/onboardings/${jwtOnbId}/files/upload`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${getToken()}` } });
      else { fd.append("token", token); res = await fetch(`${API}/client/files/upload`, { method: "POST", body: fd }); }
      if (!res.ok) throw new Error("upload");
      const j = await res.json();
      setOnb(prev => ({ ...prev, files: [...(prev.files||[]), j.file] }));
      toast.success("File uploaded");
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };
  const deleteFile = async (fid) => {
    try {
      if (isAdminMode) await api.delete(`/onboardings/${adminOnbId}/files/${fid}`);
      else if (isJwtClientMode) { toast.info("Contact Sentry Cloud IT to remove files"); return; }
      else await api.delete(`/client/files/${fid}`, { params: { token } });
      setOnb(prev => ({ ...prev, files: prev.files.filter(f => f.id !== fid) }));
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="animate-spin text-brand-primary"/></div>;
  if (!onb) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-700">Link not found</div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-xl w-full bg-white border-slate-200 p-10 text-center shadow-sm" data-testid="submitted-screen">
          <CheckCircle2 className="mx-auto text-emerald-500" size={56}/>
          <h1 className="mt-6 text-3xl font-bold tracking-tight text-brand-primary">Thank you!</h1>
          <p className="mt-3 text-slate-600 leading-relaxed">Your onboarding for <span className="font-semibold text-slate-900">{onb.company_name}</span> has been submitted to Sentry Cloud IT. Our onboarding lead will be in touch shortly to schedule your kickoff.</p>
          <div className="mt-6 text-xs text-slate-500 inline-flex items-center gap-2"><ShieldCheck size={14} className="text-brand-primary"/> Encrypted and stored securely.</div>
        </Card>
      </div>
    );
  }

  const sectionFiles = onb.files?.filter(f => f.section === section?.id) || [];
  const sectionData = data[section?.id] || {};
  const reg = section?.fields || [];
  const extras = section?.extra_fields || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 shadow-md" style={{ background: "#0F1644" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {isAdminMode && (
              <button
                onClick={() => nav(`/admin/onboarding/${adminOnbId}`)}
                className={`text-xs px-2.5 py-1 rounded border transition inline-flex items-center gap-1 ${isPreviewMode ? "border-violet-300 text-violet-200 bg-violet-500/10 hover:bg-violet-500/20" : "border-amber-300 text-amber-200 bg-amber-500/10 hover:bg-amber-500/20"}`}
                data-testid="admin-mode-badge"
                title={isPreviewMode ? "Read-only preview · back to admin view" : "Back to onboarding view (admin)"}
              >
                <ChevronLeft size={12}/>{isPreviewMode ? "Preview · back" : "Admin · back"}
              </button>
            )}
            <img src={LOGO_WIDE} alt="Sentry Cloud IT" style={{ height: 56, width: "auto" }}/>
            {onb?.msp_label && (
              <>
                <span className="text-white/40 text-xl">×</span>
                <span className="font-semibold tracking-tight" style={{ color: onb.msp_color || "#00C7FF", fontSize: 18 }} data-testid="wizard-cobrand">
                  {onb.msp_label}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 flex-1 max-w-md">
            <Progress value={progress} className="h-2 bg-white/15" data-testid="progress-bar"/>
            <span className="text-xs text-white/70 whitespace-nowrap">{progress}% complete</span>
          </div>
          {!isPreviewMode && (
            <Button onClick={() => save()} variant="ghost" size="sm" disabled={saving} className="text-white/80 hover:bg-white/10" data-testid="save-btn">
              {saving ? <Loader2 className="animate-spin" size={14}/> : <><Save size={14} className="mr-1.5"/>Save</>}
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start" data-testid="stepper">
          <Card className="bg-white border-slate-200 p-4 shadow-sm">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Sections</div>
            <nav className="space-y-1">
              {sections.map((s, i) => {
                const fs = [...(s.fields || []), ...(s.extra_fields || [])];
                const done = fs.length ? fs.filter(f => isFilled(data?.[s.id]?.[f.key])).length : 0;
                const all = fs.length;
                const status = done === 0 ? "upcoming" : done === all ? "completed" : "in-progress";
                const cls = i === idx ? "bg-[#00C7FF]/10 border-[#00C7FF] text-brand-primary font-medium"
                  : status === "completed" ? "border-emerald-200 bg-emerald-50/40 text-emerald-700 hover:bg-emerald-50"
                  : status === "in-progress" ? "border-amber-200 bg-amber-50/40 text-amber-700 hover:bg-amber-50"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50";
                return (
                  <button key={s.id} onClick={() => setIdx(i)} data-testid={`step-${s.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md border text-sm transition ${cls}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{i+1}. {s.title}</span>
                      {status === "completed" && <CheckCircle2 size={14} className="text-emerald-500 shrink-0"/>}
                    </div>
                  </button>
                );
              })}
            </nav>
          </Card>
        </aside>

        <main className="lg:col-span-9 space-y-6">
          {isPreviewMode && (
            <Card className="bg-violet-50 border-violet-200 p-4 shadow-sm" data-testid="preview-mode-banner">
              <div className="flex items-start gap-3">
                <Eye size={18} className="text-violet-600 shrink-0 mt-0.5"/>
                <div className="flex-1">
                  <div className="font-semibold text-violet-900">Preview mode · read-only</div>
                  <p className="text-sm text-violet-800 mt-0.5">You&apos;re seeing exactly what {onb?.client_name || onb?.client_email || "the client"} will see. Inputs are disabled; nothing you do here is saved. To fill the form on their behalf, use the &quot;Fill on behalf&quot; button on the admin page.</p>
                </div>
                <Button
                  onClick={() => nav(`/admin/onboarding/${adminOnbId}/edit`)}
                  size="sm"
                  className="font-semibold whitespace-nowrap"
                  style={{ background: "#00C7FF", color: "#0F1644" }}
                  data-testid="switch-to-edit-btn"
                >
                  Switch to edit mode →
                </Button>
              </div>
            </Card>
          )}
          {!isAdminMode && (
            <WelcomeBackBanner
              onb={onb}
              sections={sections}
              data={data}
              onResume={(sectionId) => {
                const i = sections.findIndex(s => s.id === sectionId);
                if (i >= 0) setIdx(i);
                setTimeout(() => {
                  const wrapper = document.querySelector("[data-resume-anchor]");
                  const empty = wrapper?.querySelector('input[value=""], textarea:empty');
                  (empty || wrapper)?.scrollIntoView({ behavior: "smooth", block: "center" });
                  empty?.focus?.();
                }, 200);
              }}
            />
          )}
          {/* fieldset disabled cascades to every nested input/select/button.
              That's why we render submit/save buttons OUTSIDE this fieldset —
              even Save/Next would otherwise become unclickable. */}
          <fieldset disabled={isPreviewMode} className="space-y-6 p-0 m-0 border-0 contents">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="inline-flex items-center gap-2 text-brand-primary text-xs uppercase tracking-[0.25em]"><Sparkles size={12}/>Section {idx + 1} of {sections.length}</div>
            {!isAdminMode && !isJwtClientMode && token && (
              <HelpRequestButton
                token={token}
                sectionId={section.id}
                sectionTitle={section.title}
                contactEmail={onb?.client_email}
              />
            )}
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-primary">{section.title}</h1>
            {section.intro && <p className="mt-3 text-slate-600 leading-relaxed max-w-3xl">{section.intro}</p>}
          </div>

          <SectionTipsCard tips={tipsBySection[section.id]} />

          <Card className="bg-white border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm" data-resume-anchor>
            {reg.length > 0 && !section.repeatable_fields && reg.map(f => (
              <Field key={f.key} field={f} value={sectionData[f.key]} onChange={(k,v)=>updateField(section.id, k, v)}/>
            ))}

            {section.repeatable_key && (
              <div className="space-y-3 pt-2">
                <div className="text-sm font-medium text-brand-primary">{section.repeatable_label}s</div>
                <RepeatableRows section={section} rows={sectionData[section.repeatable_key] || []}
                  onChange={(rows) => updateField(section.id, section.repeatable_key, rows)}/>
              </div>
            )}

            {extras.length > 0 && (
              <div className="space-y-6 pt-4 border-t border-slate-200">
                {extras.map(f => <Field key={f.key} field={f} value={sectionData[f.key]} onChange={(k,v)=>updateField(section.id, k, v)}/>)}
              </div>
            )}
          </Card>
          </fieldset>

          {!isPreviewMode && (
          <Card className="bg-white border-slate-200 p-6 shadow-sm">
            <div className="text-sm font-medium mb-2 text-brand-primary">Attach files for this section (optional)</div>
            <p className="text-xs text-slate-500 mb-3">Diagrams, screenshots, license PDFs, inventories — anything that helps us. Max 50MB each.</p>
            <label className="border-2 border-dashed border-slate-300 hover:border-[#00C7FF] bg-slate-50 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition" data-testid="file-upload-zone">
              <Upload size={20} className="text-slate-500"/>
              <span className="text-sm text-slate-700 mt-2">Click to upload</span>
              <input type="file" className="hidden" onChange={uploadFile}/>
            </label>
            {sectionFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {sectionFiles.map(f => (
                  <div key={f.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-2.5 text-sm">
                    <div className="flex items-center gap-2 min-w-0"><FileText size={14} className="text-brand-primary"/><span className="truncate">{f.filename}</span><span className="text-xs text-slate-500">{(f.size/1024).toFixed(1)} KB</span></div>
                    <Button size="sm" variant="ghost" onClick={() => deleteFile(f.id)} className="text-rose-600 hover:bg-rose-50"><Trash2 size={14}/></Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
          )}

          <div className="flex items-center justify-between pt-2">
            <Button variant="outline" onClick={prev} disabled={idx === 0} className="border-slate-300 text-slate-700" data-testid="prev-btn"><ChevronLeft size={16} className="mr-1"/>Back</Button>
            {idx < sections.length - 1 ? (
              isPreviewMode ? (
                <Button onClick={() => setIdx(idx + 1)} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="next-btn">Next section<ChevronRight size={16} className="ml-1"/></Button>
              ) : (
                <Button onClick={next} className="font-semibold" style={{ background: "#00C7FF", color: "#0F1644" }} data-testid="next-btn">Save & continue<ChevronRight size={16} className="ml-1"/></Button>
              )
            ) : !isPreviewMode ? (
              <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="submit-btn">
                {saving ? <Loader2 className="animate-spin mr-2" size={14}/> : <CheckCircle2 size={16} className="mr-1.5"/>}
                Submit to Sentry Cloud IT
              </Button>
            ) : (
              <Button onClick={() => nav(`/admin/onboarding/${adminOnbId}`)} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="preview-done-btn">
                Done previewing →
              </Button>
            )}
          </div>
        </main>
      </div>
      <BrandFooter />
    </div>
  );
}
